<template>
  <UIDialog :is-open title="Update precision amount" :z-index size="md" flat @close="handleClose">
    <div v-circular-tab class="precision-form">
      <div class="precision-form__items">
        <div class="precision-form__item-title">{{ accountTitle }}</div>
        <div class="precision-form__item-title">{{ amountTitle }}</div>
        <div class="precision-form__item-title">{{ assetTitle }}</div>
        <PrecisionFormItem
          v-for="(item, index) in amountItems"
          :key="index"
          :item
          :index
          :listValues
          :assetName
          :isJustCreated="index >= oldLength"
          @update="handleUpdateOwner"
          @delete="handleDeleteOwner"
        />
        <div v-if="listValues.length > 0" class="precision-form__button">
          <UIButton
            label="Owner"
            variant="secondary"
            size="xs"
            :leading="PlusIcon"
            @click="handleAddOwner"
          />
        </div>
        <div class="precision-form__item-total">Total</div>
        <div class="precision-form__item-total" :class="totalClasses">{{ amountFormatter(totalAmount) }}</div>
      </div>
      <UIButtonsGroup>
        <template #default="{ short }">
          <UIButtonClose :short @click="handleClose" />
          <UIButtonSave
            :short
            label="Update"
            type="submit"
            @click="handleComplete"
          />
        </template>
      </UIButtonsGroup>
    </div>
  </UIDialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { DropdownListData, ModalClass, TransactionSettings } from '@types'
import { OrganizeFamilyStructurePath } from '../utils/types'

import { ASSET_FIELD, UNKNOWN_VALUE } from '@/const'

import { displayAmount } from '@/entities/transactions/utils/helpers'

import { useAssetsBunchStore } from '@/store/assets/bunch'

import { UIDialog } from '@ui/modals'
import { UIButton, UIButtonClose, UIButtonSave, UIButtonsGroup } from '@ui/buttons'
import { PlusIcon } from '@heroicons/vue/24/outline'
import PrecisionFormItem from './PrecisionFormItem.vue'

type Props = {
  modal: ModalClass<any>
  entrySettings: TransactionSettings['entries'][0]
  assetId: string
  paths: OrganizeFamilyStructurePath[]
}

type Emits = {
  'complete': [data: OrganizeFamilyStructurePath[]]
  'cancel': []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const assetsBunchStore = useAssetsBunchStore()

const amountItems = ref<OrganizeFamilyStructurePath[]>([])
const oldLength = ref(0)

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const assetName = computed(() => assetsBunchStore.getElementById(props.assetId)?.field<string>(ASSET_FIELD.NAME).value || UNKNOWN_VALUE)

const accountTitle = computed(() => props.entrySettings.account?.title)
const amountTitle = computed(() => props.entrySettings.amount?.title)
const assetTitle = computed(() => props.entrySettings.asset?.title)

const assetPaths = computed(() => amountItems.value
  .map(item => item.path)
  .filter(path => path.at(-1) === props.assetId)
)

const isPathPrefix = (prefix: string[], path: string[]) => {
  return prefix.length <= path.length && prefix.every((item, index) => path[index] === item)
}

const getPathLabel = (path: string[]) => {
  return path
    .slice(1)
    .map(item => assetsBunchStore.getElementById(item)?.field<string>(ASSET_FIELD.NAME).value)
    .join(' -> ')
}

const listValues = computed(() => props.paths.reduce((acc, path) => {
  const isUnavailablePath = assetPaths.value.some(assetPath => {
    return isPathPrefix(path.path, assetPath) || isPathPrefix(assetPath, path.path)
  })

  if (!isUnavailablePath) {
    acc.push({
      value: JSON.stringify([...path.path, props.assetId]),
      label: getPathLabel(path.path),
    })
  }
  return acc
}, [] as DropdownListData[]))

const totalAmount =  computed(() => {
  return amountItems.value.reduce((acc, value) => {
    return acc + value.amount
  }, 0)
})

const totalClasses = computed(() => ({
  'precision-form__item-total--positive':  totalAmount.value <= 1,
  'precision-form__item-total--negative':  totalAmount.value > 1,
}))

const amountFormatter = (value: number | null | undefined) => {
  const settings = props.entrySettings.amount
  if (!settings) return ''
  const isPercent = settings?.rule === 'percent'
  return displayAmount(value, isPercent)
}

const handleClose = () => {
  emit('cancel')
  props.modal.close()
}

const handleComplete = () => {
  emit('complete', amountItems.value)
  props.modal.close()
}

const handleAddOwner = () => {
  amountItems.value.push({ path: [], amount: 0 })
}

const handleUpdateOwner = (index: number, value: OrganizeFamilyStructurePath) => {
  amountItems.value[index] = value
}

const handleDeleteOwner = (index: number) => {
  amountItems.value.splice(index, 1)
}

watch(() => props.paths, value => {
  amountItems.value = value.filter(el => el.path.at(-1) === props.assetId)
  oldLength.value = amountItems.value.length
}, { immediate: true })
</script>

<style scoped>
.precision-form {
  @apply pt-4 sm:pt-0;

  &__items {
    @apply grid grid-cols-[1fr_min-content_1fr];

    :deep(> div) {
      @apply px-1;

      &:nth-child(3n + 1) {
        @apply pl-default;
      }

      &:nth-child(3n) {
        @apply pr-default;
      }
    }
  }

  &__item-title,
  &__item-total {
    @apply py-2;
    @apply bg-body-gray;
    @apply text-light-text;
    @apply text-sm font-medium;
  }

  &__item-title {
    @apply hidden sm:flex items-center;
    @apply whitespace-normal leading-none;
    @apply mb-1 py-1;
  }

  &__item-total {

    &--positive {
      @apply text-gray-950 dark:text-green-400;
    }
    &--negative {
      @apply text-red-600 dark:text-red-400;
    }

    & + & {
      @apply col-span-2;
    }
  }

  &__button {
    @apply col-span-3 mt-2 mb-2;
    @apply !pr-default;

    .ui-button {
      @apply w-full;
    }
  }

  &__button + &__item-total {
    @apply !pl-default;
  }

  .ui-buttons-group {
    @apply py-4 px-default;
  }
}
</style>
